import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonGroup, Checkbox, Fade, FormControlLabel, FormGroup, Stack, Tooltip, Typography } from '@mui/material';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FlagIcon from '@mui/icons-material/Flag';
import SouthAmericaIcon from '@mui/icons-material/SouthAmerica';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MapIcon from '@mui/icons-material/Map';

import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import DeleteIcon from '@mui/icons-material/Delete';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

import { Lookups } from '../../utils';

const pillButtonStyle = {
    bgcolor: '#ffffff',
    color: '#000000',
    borderRadius: '8px',
    height: '40px',
    textTransform: 'none',
    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
    ':hover': {
        opacity: 0.95,
        bgcolor: '#ffffff'
    }
};

const locationButtonStyle = {
    padding: '0 8px',
    background: '#FFFFFF',
    height: '40px',
    textTransform: 'none',
    marginLeft: '12px',
    backgroundColor: '#FFFFFF',
    color: 'black',
    border: 'none',
    borderRadius: '8px',
    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px'
};

const locationButtonIconStyle = {
    height: '18px',
    marginTop: '4px'
};

const MapOption = ({ OnSelectionChange, OnWorldWideClick, OnAreaOptionClick, OnSeeUpdateClick }) => {
    const [selectedLayer, setLayer] = useState(Lookups.MapLayer.Regulatory);
    const [selectedOptions, setOptions] = useState([Lookups.MapRegulatoryGroup.Country]);

    useEffect(() => {
        OnSelectionChange(selectedLayer, selectedOptions);
    }, [selectedLayer, selectedOptions]);

    const handleLayerChange = (layer) => {
        setLayer(layer);
    };

    const handleWorldWideClick = () => {
        OnWorldWideClick();
    };

    const handleRecentUpdatesClick = () => {
        OnSeeUpdateClick();
    };

    const handleCheckChange = (option, checked) => {
        if (checked) {
            // Add checked option
            setOptions([...selectedOptions, option]);
        } else {
            // Remove checked option
            setOptions((items) => items.filter((f) => f != option));
        }
    };

    const handleAreaOptionChange = (option) => {
        if (option === Lookups.MapAreaGroup.ECA) {
            OnAreaOptionClick('ECA');
        } else if (option === Lookups.MapAreaGroup.PSSA) {
            OnAreaOptionClick('PSSA');
        } else if (option === Lookups.MapAreaGroup.SpecialGarbage) {
            OnAreaOptionClick('Garbage');
        } else if (option === Lookups.MapAreaGroup.SpecialOil) {
            OnAreaOptionClick('Oil');
        } else if (option === Lookups.MapAreaGroup.SpecialSewage) {
            OnAreaOptionClick('Sewage');
        }
    };

    return (
        <>
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    height: '40px'
                }}
            >
                {/* Regulatory, Area, Fuels, and Incentive Layer */}
                <ButtonGroup variant="text" aria-label="outlined primary button group" value={selectedLayer}>
                    {/* Regulatory Button */}
                    <Button
                        sx={{
                            ...pillButtonStyle,
                            ...(selectedLayer === Lookups.MapLayer.Regulatory && {
                                // Remove Shadow when Button is Selected
                                boxShadow: '0 0 0 0.1rem #002A4D'
                            })
                        }}
                        value={Lookups.MapLayer.Regulatory}
                        onClick={(e) => handleLayerChange(e.target.value)}
                    >
                        Regulatory
                    </Button>

                    {/* Fuels button */}
                    <Button
                        sx={{
                            ...pillButtonStyle,
                            ...(selectedLayer === Lookups.MapLayer.Fuels && {
                                // Remove Shadow when Button is Selected
                                boxShadow: '0 0 0 0.1rem #002A4D'
                            })
                        }}
                        value={Lookups.MapLayer.Fuels}
                        onClick={(e) => handleLayerChange(e.target.value)}
                    >
                        Alt. Fuels
                    </Button>

                    {/* Incetives Button */}
                    <Button
                        sx={{
                            ...pillButtonStyle,
                            marginLeft: '1px',
                            ...(selectedLayer === Lookups.MapLayer.Incentive && {
                                // Remove Shadow when Button is Selected
                                boxShadow: '0 0 0 0.1rem #002A4D'
                            })
                        }}
                        value={Lookups.MapLayer.Incentive}
                        onClick={(e) => handleLayerChange(e.target.value)}
                    >
                        Incentives
                    </Button>
                </ButtonGroup>

                {/* Regulatory Layer Options */}
                {selectedLayer === Lookups.MapLayer.Regulatory && (
                    <>
                        {/* WorldWide */}
                        <Box sx={locationButtonStyle}>
                            <Button style={{ textTransform: 'none', color: 'inherit' }} startIcon={<TravelExploreIcon />} onClick={() => handleWorldWideClick()}>
                                <Typography variant="subtitle1">Worldwide</Typography>
                            </Button>
                        </Box>

                        {/* Recent Updates */}
                        <Box sx={locationButtonStyle}>
                            <Button
                                style={{
                                    textTransform: 'none',
                                    color: 'inherit',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                startIcon={<PendingActionsIcon />}
                                onClick={() => handleRecentUpdatesClick()}
                            >
                                <Typography variant="subtitle1">Recent Updates</Typography>
                            </Button>
                        </Box>

                        {/* Regions */}
                        <Box sx={locationButtonStyle}>
                            <FormGroup>
                                <FormControlLabel
                                    label={
                                        <Stack direction="row">
                                            <SouthAmericaIcon sx={locationButtonIconStyle} />
                                            <Typography variant="subtitle1">Regions</Typography>
                                        </Stack>
                                    }
                                    control={
                                        <Checkbox
                                            color="primary"
                                            key={Lookups.MapRegulatoryGroup.Region}
                                            checked={selectedOptions.includes(Lookups.MapRegulatoryGroup.Region)}
                                            onChange={(event) => {
                                                handleCheckChange(Lookups.MapRegulatoryGroup.Region, event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            </FormGroup>
                        </Box>

                        {/* Countries */}
                        <Box sx={locationButtonStyle}>
                            <FormGroup>
                                <FormControlLabel
                                    label={
                                        <Stack direction="row">
                                            <FlagIcon sx={locationButtonIconStyle} />
                                            <Typography variant="subtitle1">Countries</Typography>
                                        </Stack>
                                    }
                                    control={
                                        <Checkbox
                                            color="primary"
                                            key={Lookups.MapRegulatoryGroup.Country}
                                            checked={selectedOptions.includes(Lookups.MapRegulatoryGroup.Country)}
                                            onChange={(event) => {
                                                handleCheckChange(Lookups.MapRegulatoryGroup.Country, event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            </FormGroup>
                        </Box>

                        {/* States */}
                        <Box sx={locationButtonStyle}>
                            <FormGroup>
                                <FormControlLabel
                                    label={
                                        <Stack direction="row">
                                            <AccountBalanceIcon sx={locationButtonIconStyle} />
                                            <Typography variant="subtitle1">States</Typography>
                                        </Stack>
                                    }
                                    control={
                                        <Checkbox
                                            color="primary"
                                            key={Lookups.MapRegulatoryGroup.State}
                                            checked={selectedOptions.includes(Lookups.MapRegulatoryGroup.State)}
                                            onChange={(event) => {
                                                handleCheckChange(Lookups.MapRegulatoryGroup.State, event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            </FormGroup>
                        </Box>

                        {/* Ports */}
                        <Box sx={locationButtonStyle}>
                            <FormGroup>
                                <FormControlLabel
                                    label={
                                        <Stack direction="row">
                                            <DirectionsBoatIcon sx={locationButtonIconStyle} />
                                            <Typography variant="subtitle1">Ports</Typography>
                                        </Stack>
                                    }
                                    control={
                                        <Checkbox
                                            color="primary"
                                            key={Lookups.MapRegulatoryGroup.Port}
                                            checked={selectedOptions.includes(Lookups.MapRegulatoryGroup.Port)}
                                            onChange={(event) => {
                                                handleCheckChange(Lookups.MapRegulatoryGroup.Port, event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            </FormGroup>
                        </Box>
                    </>
                )}

                {/* Area Layer Options */}
                {selectedLayer === Lookups.MapLayer.Area && (
                    <>
                        {/* ECAs */}
                        <Tooltip title="Emission Control Areas" placement="bottom" TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                            <Box sx={locationButtonStyle}>
                                <Button
                                    style={{ textTransform: 'none', color: 'inherit' }}
                                    startIcon={<MapIcon />}
                                    onClick={(e) => handleAreaOptionChange(e.currentTarget.value)}
                                    value={Lookups.MapAreaGroup.ECA}
                                >
                                    <Typography variant="subtitle1">ECA</Typography>
                                </Button>
                            </Box>
                        </Tooltip>

                        {/* PSSA */}
                        <Tooltip title="Particularly Sensitive Sea Area" placement="bottom" TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                            <Box sx={locationButtonStyle}>
                                <Button
                                    style={{ textTransform: 'none', color: 'inherit' }}
                                    startIcon={<MapIcon />}
                                    onClick={(e) => handleAreaOptionChange(e.currentTarget.value)}
                                    value={Lookups.MapAreaGroup.PSSA}
                                >
                                    <Typography variant="subtitle1">PSSA</Typography>
                                </Button>
                            </Box>
                        </Tooltip>

                        {/* Special Area - Garbage */}
                        <Tooltip title="Special Area - Garbage" placement="bottom" TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                            <Box sx={locationButtonStyle}>
                                <Button
                                    style={{ textTransform: 'none', color: 'inherit' }}
                                    startIcon={<DeleteIcon />}
                                    onClick={(e) => handleAreaOptionChange(e.currentTarget.value)}
                                    value={Lookups.MapAreaGroup.SpecialGarbage}
                                >
                                    <Typography variant="subtitle1">Special - Garbage</Typography>
                                </Button>
                            </Box>
                        </Tooltip>

                        {/* Special Area - Oil */}
                        <Tooltip title="Special Area - Oil" placement="bottom" TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                            <Box sx={locationButtonStyle}>
                                <Tooltip title="Special Area - Oil" placement="bottom">
                                    <Button
                                        style={{ textTransform: 'none', color: 'inherit' }}
                                        startIcon={<OilBarrelIcon />}
                                        onClick={(e) => handleAreaOptionChange(e.currentTarget.value)}
                                        value={Lookups.MapAreaGroup.SpecialOil}
                                    >
                                        <Typography variant="subtitle1">Special - Oil</Typography>
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Tooltip>

                        {/* Special Area - Sewage */}
                        <Tooltip title="Special Area - Sewage" placement="bottom" TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                            <Box sx={locationButtonStyle}>
                                <Button
                                    style={{ textTransform: 'none', color: 'inherit' }}
                                    startIcon={<WaterDropIcon />}
                                    onClick={(e) => handleAreaOptionChange(e.currentTarget.value)}
                                    value={Lookups.MapAreaGroup.SpecialSewage}
                                >
                                    <Typography variant="subtitle1">Special - Sewage</Typography>
                                </Button>
                            </Box>
                        </Tooltip>
                    </>
                )}
            </Stack>
        </>
    );
};

export default MapOption;
